import {
    CoreDI,
    Device,
    DeviceType,
    DI,
    DisplayObjectUtils,
    GameConfig,
    IBrowser, IEventManagerWithAsync,
    InfoModule,
    inject,
    injectable,
    ObjectBuilder,
    ResizeEventData,
    ResourceManager,
    ScreenManager,
    ScreenOrientation,
    Scroll,
    SlotSettings,
} from '@gongxigames/framework/slot';
import * as Hogan from 'hogan.js';
import {Sprite} from 'pixi.js';
import {GameOnlyEventMap, slotStore, strictUiEmitter} from '@gongxigames/framework/slotV2/globals';
import {strictCoreEmitter} from '@gongxigames/framework/core/globals';
import {uiMenuStore} from '@gongxigames/framework/slotV2/ui/services/UIMenu/uiMenuStore';
import {SlotModuleV2} from '@gongxigames/framework/slotV2/baseClasses/slotModuleV2/SlotModuleV2';

export type InfoSystemData = {
    name: symbol;
    content: HTMLElement;
    title?: string;
    scroll?: boolean;
};

@injectable()
export class InfoSystemModule<T extends GameOnlyEventMap> extends SlotModuleV2<T> {
    public static readonly NAME: symbol = Symbol('InfoSystemModule');

    @inject(DI.ResourceManager)
    protected _resourceManager!: ResourceManager;

    @inject(CoreDI.Device)
    protected _device!: Device;

    @inject(CoreDI.Settings)
    protected _settings!: SlotSettings;

    protected contentClassName = '.content-info-system';
    protected currentScroll?: Scroll;
    protected currentContainer!: HTMLElement;
    protected currentPage!: HTMLElement;
    protected domContent!: HTMLElement;
    protected title!: HTMLElement;
    protected spinner!: HTMLElement;
    protected isSpinnerShown = false;
    protected isScrollVisible = false;

    private readonly offset = {
        top: {
            [ScreenOrientation.PORTRAIT]: uiMenuStore.heightPortrait.peek() * 0.76,
            [ScreenOrientation.LANDSCAPE]: uiMenuStore.heightLandscape.peek() * 0.67,
        },
        bottom: {
            [ScreenOrientation.PORTRAIT]: uiMenuStore.heightPortrait.peek(),
            [ScreenOrientation.LANDSCAPE]: uiMenuStore.heightLandscape.peek(),
        }
    } as const;

    @inject(CoreDI.Browser)
    protected _browser!: IBrowser;

    @inject(CoreDI.ObjectBuilder)
    protected _objectBuilder!: ObjectBuilder;

    @inject(CoreDI.ScreenManager)
    protected _screenManager!: ScreenManager;

    @inject(CoreDI.DisplayObjectUtils)
    protected _displayObjectUtils!: DisplayObjectUtils;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    @inject(CoreDI.IEventManagerWithAsync)
    protected _eventManager!: IEventManagerWithAsync;

    public async init(): Promise<void> {
        super.init();

        const templateString = await this._resourceManager.loadTemplate('infoPageSystem');
        const template = Hogan.compile(templateString);
        const html = template.render({});

        document.body.insertAdjacentHTML('beforeend', html);

        this.domContent = document.querySelector(this.contentClassName)!;
        this.currentContainer = document.getElementById('infoSystemContainer')!;
        this.currentPage = document.getElementById('infoSystem')!;
        this.title = document.getElementById('infoSystemTitle')!;
        this.spinner = document.querySelector('.spinnerContainer')!;

        this.createSpinner();
    }

    protected onSetupEvents(): void {
        strictCoreEmitter.on('CORE.screen.resize', this.onOrientationChanged.bind(this));
        strictUiEmitter.on('UI.menu.closed', this.close.bind(this));

        this._eventManager.on('General.Popups.Add', this.close.bind(this), this);

        this.emitter.on('UI.command.infoSystem.wait', this.onWait.bind(this));
        this.emitter.on('UI.command.infoSystem.show', this.onPagesShow.bind(this));
    }

    protected blur(): void {
        document.body.classList.add('blurred-canvas');
    }

    protected onWait(): void {
        if (slotStore.isDisabledUIMode.value && slotStore.disabledUIModeDataName.value &&
            !slotStore.infoSystemData.value.get(slotStore.disabledUIModeDataName.value)) {
            this.showSpinner();
        }
    }

    protected onPagesShow(): void {
        if (!slotStore.isDisabledUIMode.value || !slotStore.disabledUIModeDataName.value) {
            return;
        }
        const infoPageName = slotStore.disabledUIModeDataName.value;
        const {content, title, scroll} = slotStore.infoSystemData.value.get(infoPageName)!;
        const page = content.getElementsByClassName('content')[0];

        this.hideSpinner();
        content.classList.add('open');

        if (page) {
            const parent = page.parentElement;

            while (parent?.firstChild) {
                parent.removeChild(parent.firstChild);
            }
            parent?.appendChild(page);
            this.domContent.appendChild(content);
            this.title.innerHTML = title ?? '';
        }

        this.open();
        this.handleScroll(scroll);
    }

    protected open(): void {
        this.currentContainer!.classList.add('open');

        this.onResized();
        window.scrollBy(0, window.scrollY === 1 ? 0 : 1);

        strictUiEmitter.emit('Game.command.round.uiDisabled', InfoModule.NAME);
    }

    protected handleScroll(shouldHave = true): void {
        if (!shouldHave) {
            this.currentScroll?.hide();
            this.isScrollVisible = false;

            return;
        }

        if (this.currentScroll) {
            this.isScrollVisible = true;
            this.currentScroll.show();
            this.resizeScroll();
        } else {
            this.isPlatform(DeviceType.DESKTOP) && this.createCustomScroll();
        }
    }

    protected close(): void {
        this.currentContainer && this.currentContainer.classList.remove('open');

        if (this.isPlatform(DeviceType.DESKTOP)) {
            this.store.soundPlayer.value!.play('buttonclick');
        }

        this.title.innerHTML = '';
        this.currentScroll?.hide();

        strictUiEmitter.emit('Game.command.round.uiEnabled');
        strictUiEmitter.emit('Game.command.hideInfo');

        Array.from(this.domContent.children).forEach(child => child.classList.remove('open'));
        document.body.classList.remove('blurred-canvas');
    }

    protected hideSpinner(): void {
        if (this.isSpinnerShown) {
            this.isSpinnerShown = false;
            this.spinner.classList.remove('open');
        }
    }

    protected showSpinner(): void {
        this.isSpinnerShown = true;
        this.currentContainer.classList.add('open');
        this.spinner.classList.add('open');
    }

    protected createSpinner(): void {
        const loaderContainer = this._objectBuilder.buildSprite(ObjectBuilder.resources.shared_sprite.waitingPopup);
        const loadingIcon = loaderContainer.children[0] as Sprite;
        const base64 = this._displayObjectUtils.spriteToBase64(loadingIcon);

        this.spinner.innerHTML = `<img alt="spinner" src="${base64}" class="spinner"/>`;
    }

    protected onResized(): void {
        requestAnimationFrame(() => {
            this.fitToGameDimensions();
            this.setContentSize();

            if (this.isPlatform(DeviceType.DESKTOP) && this.currentScroll) {
                this._browser.addTimeout(() => this.resizeScroll());
            }
        });
    }

    protected onOrientationChanged({orientation}: ResizeEventData): void {
        if (orientation === ScreenOrientation.PORTRAIT) {
            document.body.classList.add('portrait');
            document.body.classList.remove('landscape');
        } else {
            document.body.classList.add('landscape');
            document.body.classList.remove('portrait');
        }

        this.onResized();
    }

    protected createCustomScroll(): void {
        this.isScrollVisible = true;
        this.currentScroll = new Scroll(this.currentPage, this._device, {contentClass: this.contentClassName});
    }

    protected resizeScroll(): void {
        this.isScrollVisible && this.currentScroll?.onResize();
    }

    protected setContentSize(): void {
        const pixelRatio = this._device.getPixelRatio();
        const orientation = slotStore.stage_isPortrait.peek() ? ScreenOrientation.PORTRAIT : ScreenOrientation.LANDSCAPE;
        const {options: {contentSize: {width, height}}, position: {x, y}, scale} = slotStore.stage_safeZoneState_ui.peek();
        const offsetTop = this.getOffset(orientation, 'top') * scale;
        const offsetBottom = this.getOffset(orientation, 'bottom') * scale;

        const resW = width * scale / pixelRatio;
        const resH = (height * scale - offsetTop - offsetBottom + (y * 2)) / pixelRatio;
        const resY = offsetTop / pixelRatio;
        const resX = x / pixelRatio;

        this.currentContainer.style.height = `${resH + resY + 1}px`;

        if (this.isPlatform(DeviceType.MOBILE)) {
            this.setContentSizeProps(resX, resY, resW, resH);
        }
    }

    protected fitToGameDimensions(): void {
        if (this.isPlatform(DeviceType.DESKTOP) && this.currentPage) {
            const state = this._screenManager.getScreenState();
            const pixelRatio = this._device.getPixelRatio();
            const scale = state.width * state.scale / pixelRatio / this._gameConfig.viewport.desktop.width;

            this.title.style.fontSize = 100 * scale + 'px';
            this.currentPage.style.fontSize = 100 * scale + 'px';

            this.resizeScroll();
        }
    }

    private setContentSizeProps(x: number, y: number, width: number, height: number): void {
        const content = this.currentPage?.querySelector(this.contentClassName) as any;

        if (!content) {
            return;
        }

        content.style.top = y + 'px';
        content.style.left = x + 'px';
        content.style.width = width + 'px';
        content.style.height = height + 'px';

        content.scrollTo(0, 0);
        // hack for CLFR-2308
        requestAnimationFrame(() => content.scrollTo(0, 0));
    }

    private getOffset(orientation: ScreenOrientation, position: 'top' | 'bottom'): number {
        return this.offset[position][orientation];
    }

    protected isPlatform(platform: DeviceType): boolean {
        return this._settings.get('platform') === platform;
    }

    public destroy(): void {
        super.destroy();

        if (this.currentContainer && this.currentContainer.parentElement) {
            this.currentContainer.parentElement.removeChild(this.currentContainer);
        }

        this.currentScroll?.removeListeners();
    }
}
